import React, { Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Image as ImageIcon,
  DeleteOutline as DeleteOutlineIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import axios from 'axios';
import {
  analyzeBloodTestFiles,
  type BloodTestItem,
  type BloodTestResult,
} from '../api/bloodTest';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import {
  DEFAULT_CLIPBOARD_FORMAT_OPTIONS,
  formatBloodTestForClipboard,
  formatBloodTestPlainText,
  type BloodTestClipboardFormatOptions,
} from '../utils/bloodTestClipboard';
import { buildResultFromEdits, createItemValueEdits, parseItemEditInput } from '../utils/bloodTestItemEdit';
import { computeAbnormalityFromReference } from '../utils/bloodTestReference';

// pdf.js が大きいため、結果表示時にのみ読み込む
const BloodTestSourcePreview = lazy(() => import('./BloodTestSourcePreview'));

const ACCEPT = 'application/pdf,image/png,image/jpeg,image/gif,image/webp';
const ALLOWED_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 5;
const MAX_TOTAL_BYTES = 30 * 1024 * 1024;

const STEPS = ['ファイルを選択', 'AI解析中', '結果を表示'];

function cloneBloodTestResult(data: BloodTestResult): BloodTestResult {
  return {
    hospital_name: data.hospital_name,
    collection_date: data.collection_date,
    items: data.items.map((item) => ({ ...item })),
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const BloodTestAnalysis: React.FC = () => {
  const { showSnackbar } = useAppSnackbar();
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BloodTestResult | null>(null);
  const [itemValueEdits, setItemValueEdits] = useState<Record<string, string>>({});
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  // 原本と照合済みかの一時的なメモ（保存はしない。再解析・クリアでリセット）
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});
  const [clipboardFormat, setClipboardFormat] = useState<BloodTestClipboardFormatOptions>(
    DEFAULT_CLIPBOARD_FORMAT_OPTIONS
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const activeStep = result ? 2 : analyzing ? 1 : 0;

  const clipboardPreviewPlain = useMemo(() => {
    if (!result) return '';
    return formatBloodTestPlainText(buildResultFromEdits(result, itemValueEdits));
  }, [result, itemValueEdits]);

  /** ホバー中の検査項目（読み取り位置の強調表示用。値はAI抽出時の原本値を使う） */
  const highlightItem = useMemo<BloodTestItem | null>(() => {
    if (!result || !highlightKey) return null;
    return result.items.find((i) => i.key === highlightKey) ?? null;
  }, [result, highlightKey]);

  const addFiles = useCallback(
    (incoming: FileList | null) => {
      if (!incoming || incoming.length === 0) return;
      // 受理リストとフラグは setState の外で計算し、updater は純粋に保つ（StrictMode の二重実行や副作用の重複を避ける）
      const next = [...files];
      let rejectedType = false;
      let rejectedSize = false;
      let overflowCount = false;
      let overflowTotal = false;
      for (const f of Array.from(incoming)) {
        if (!ALLOWED_TYPES.has(f.type)) {
          rejectedType = true;
          continue;
        }
        if (f.size > MAX_FILE_BYTES) {
          rejectedSize = true;
          continue;
        }
        const dup = next.some(
          (e) => e.name === f.name && e.size === f.size && e.lastModified === f.lastModified
        );
        if (dup) continue;
        if (next.length >= MAX_FILES) {
          overflowCount = true;
          continue;
        }
        const total = next.reduce((s, x) => s + x.size, 0) + f.size;
        if (total > MAX_TOTAL_BYTES) {
          overflowTotal = true;
          continue;
        }
        next.push(f);
      }
      setFiles(next);
      if (rejectedType) {
        showSnackbar({ message: 'PDF・画像ファイルのみ追加できます', severity: 'warning' });
      }
      if (rejectedSize) {
        showSnackbar({
          message: `1ファイルあたり ${MAX_FILE_BYTES / (1024 * 1024)}MB までです`,
          severity: 'warning',
        });
      }
      if (overflowCount) {
        showSnackbar({ message: `ファイルは最大 ${MAX_FILES} 件までです`, severity: 'warning' });
      }
      if (overflowTotal) {
        showSnackbar({
          message: `合計サイズは ${MAX_TOTAL_BYTES / (1024 * 1024)}MB までです`,
          severity: 'warning',
        });
      }
    },
    [files, showSnackbar]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragActive(false);
      if (analyzing) return;
      addFiles(e.dataTransfer.files);
    },
    [addFiles, analyzing]
  );

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAnalyze = async () => {
    if (files.length === 0) return;
    setAnalyzing(true);
    setError('');
    setResult(null);
    setItemValueEdits({});
    setHighlightKey(null);
    setCheckedItems({});
    try {
      const data = await analyzeBloodTestFiles(files);
      const cloned = cloneBloodTestResult(data);
      setResult(cloned);
      setItemValueEdits(createItemValueEdits(cloned.items));
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : e instanceof Error
            ? e.message
            : null;
      setError(msg || '解析に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setResult(null);
    setItemValueEdits({});
    setHighlightKey(null);
    setCheckedItems({});
    setError('');
  };

  const toggleItemChecked = (key: string) => {
    setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopyToClipboard = async () => {
    if (!result) return;
    const text = formatBloodTestForClipboard(
      buildResultFromEdits(result, itemValueEdits),
      clipboardFormat
    );
    if (!text) {
      showSnackbar({ message: 'コピーできる検査値がありません', severity: 'warning' });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showSnackbar({ message: 'クリップボードにコピーしました', severity: 'success' });
    } catch {
      showSnackbar({ message: 'クリップボードへのコピーに失敗しました', severity: 'error' });
    }
  };

  const toggleClipboardFormat = (key: keyof BloodTestClipboardFormatOptions) => {
    setClipboardFormat((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateResultField = (field: 'hospital_name' | 'collection_date', value: string) => {
    setResult((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value.trim() || null };
    });
  };

  const updateResultItemValue = (key: string, value: string) => {
    setItemValueEdits((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 0.5 }}>
        採血結果解析
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        採血結果をアップロードし、AIで検査値・採血日・病院名を読み取ります。
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3, maxWidth: 560 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {!analyzing && !result && (
        <>
          {/* ドラッグ&ドロップ領域 */}
          <Paper
            variant="outlined"
            onClick={() => inputRef.current?.click()}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
            sx={{
              p: 4,
              mb: 2,
              textAlign: 'center',
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: dragActive ? 'primary.main' : 'divider',
              bgcolor: dragActive ? 'action.hover' : 'background.paper',
              transition: 'border-color .15s, background-color .15s',
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              ここにファイルをドラッグ＆ドロップ
            </Typography>
            <Typography variant="body2" color="text.secondary">
              またはクリックして選択
            </Typography>
          </Paper>

          {files.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {files.map((f, idx) => (
                <Chip
                  key={`${f.name}-${f.size}-${f.lastModified}`}
                  icon={f.type === 'application/pdf' ? <DescriptionIcon /> : <ImageIcon />}
                  label={`${f.name}（${formatBytes(f.size)}）`}
                  onDelete={() => removeFile(idx)}
                  deleteIcon={<DeleteOutlineIcon />}
                  variant="outlined"
                  sx={{ maxWidth: 320 }}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<ScienceIcon />}
              disabled={files.length === 0}
              onClick={() => void handleAnalyze()}
            >
              解析
            </Button>
            <Button
              variant="text"
              disabled={files.length === 0 && !error}
              onClick={handleReset}
            >
              クリア
            </Button>
          </Stack>
        </>
      )}

      {result && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              コピー時の装飾:
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => toggleClipboardFormat('underline')}
              sx={(t) => ({
                minWidth: 36,
                px: 0.75,
                color: 'text.primary',
                borderColor: 'text.secondary',
                ...(clipboardFormat.underline && {
                  bgcolor: alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.14 : 0.08),
                  borderColor: t.palette.text.secondary,
                }),
              })}
              aria-label="下線"
              aria-pressed={clipboardFormat.underline}
            >
              <FormatUnderlinedIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => toggleClipboardFormat('redText')}
              sx={(t) => ({
                minWidth: 36,
                px: 0.75,
                ...(clipboardFormat.redText && {
                  bgcolor: alpha(t.palette.error.main, t.palette.mode === 'dark' ? 0.22 : 0.12),
                  borderColor: t.palette.error.main,
                  color: t.palette.error.main,
                }),
              })}
              aria-label="赤字"
              aria-pressed={clipboardFormat.redText}
            >
              赤
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => void handleCopyToClipboard()}
              sx={{ ml: 1 }}
            >
              クリップボードにコピー
            </Button>
            <Box sx={{ flex: 1, minWidth: 16 }} />
            <Button variant="text" size="small" onClick={handleReset}>
              最初からやり直す
            </Button>
          </Box>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'action.hover',
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              コピープレビュー
            </Typography>
            {clipboardPreviewPlain ? (
              <Typography
                component="div"
                variant="body2"
                sx={{
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  ...(clipboardFormat.redText && { color: 'error.main' }),
                  ...(clipboardFormat.underline && { textDecoration: 'underline' }),
                }}
              >
                {clipboardPreviewPlain}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.disabled">
                （コピーする内容がありません）
              </Typography>
            )}
          </Paper>
        </Stack>
      )}

      {analyzing && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            AIが最新採血日の結果を読み取っています。
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {result && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              lg: 'minmax(480px, 0.9fr) minmax(440px, 1.1fr)',
              xl: 'minmax(580px, 0.9fr) minmax(620px, 1.1fr)',
            },
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, flex: 1, minWidth: 0, width: '100%' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 4 }}
            sx={{ mb: 2 }}
          >
            <TextField
              label="病院名・検査施設"
              size="small"
              value={result.hospital_name ?? ''}
              onChange={(e) => updateResultField('hospital_name', e.target.value)}
              sx={{ minWidth: { sm: 220 }, flex: 1 }}
            />
            <TextField
              label="採血日"
              size="small"
              placeholder="YYYY-MM-DD"
              value={result.collection_date ?? ''}
              onChange={(e) => updateResultField('collection_date', e.target.value)}
              sx={{ minWidth: { sm: 160 }, flex: 1 }}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <TableContainer>
            <Table size="small" sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    確認
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>項目</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    値
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>単位</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>備考</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.items.map((item) => {
                  const isComputed = item.key === '補正Ca';
                  const isChecked = Boolean(checkedItems[item.key]);
                  const parsed = parseItemEditInput(itemValueEdits[item.key] ?? '');
                  const abnormality = computeAbnormalityFromReference(item.key, parsed.value);
                  return (
                    <TableRow
                      key={item.key}
                      onMouseEnter={() => setHighlightKey(item.key)}
                      onMouseLeave={() =>
                        setHighlightKey((prev) => (prev === item.key ? null : prev))
                      }
                      sx={(t) => ({
                        ...(isComputed && { bgcolor: 'action.hover' }),
                        ...(isChecked && {
                          bgcolor: alpha(
                            t.palette.success.main,
                            t.palette.mode === 'dark' ? 0.14 : 0.08
                          ),
                        }),
                        '&:hover': { bgcolor: 'action.selected' },
                      })}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          color="success"
                          checked={isChecked}
                          onChange={() => toggleItemChecked(item.key)}
                          inputProps={{ 'aria-label': `${item.label}を確認済みにする` }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: isComputed ? 600 : 400 }}>
                        {item.label}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.75 }}>
                        <TextField
                          size="small"
                          variant="standard"
                          value={itemValueEdits[item.key] ?? ''}
                          onChange={(e) => updateResultItemValue(item.key, e.target.value)}
                          placeholder="—"
                          inputProps={{
                            'aria-label': `${item.label}の値`,
                            sx: {
                              textAlign: 'right',
                              ...(abnormality === 'H' && { color: 'error.main' }),
                              ...(abnormality === 'L' && { color: 'info.main' }),
                            },
                          }}
                          sx={{ width: '100%', maxWidth: 140, ml: 'auto' }}
                        />
                      </TableCell>
                      <TableCell>{item.unit || ''}</TableCell>
                      <TableCell>
                        {item.note ? (
                          <Typography variant="caption" color="text.secondary">
                            {item.note}
                          </Typography>
                        ) : (
                          ''
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            ※ AIによる読み取り結果です。カルテ登録前に必ず原本と照合し、必要に応じて値を修正してください。基準値を下回る値は青、上回る値は赤で表示されます（L/H
            はコピー時に値の末尾へ付与されます。空欄にするとコピー対象から除外されます）。確認列のチェックは照合作業用の一時的なメモで、保存されません。
          </Typography>
        </Paper>

        {/* 原本プレビュー。行ホバーで読み取り位置を赤枠表示する */}
        <Paper
          variant="outlined"
          sx={{
            width: '100%',
            minWidth: 0,
            position: { xs: 'static', lg: 'sticky' },
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2">原本プレビュー</Typography>
          </Box>
          <Suspense
            fallback={
              <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                プレビューを読み込んでいます…
              </Typography>
            }
          >
            <BloodTestSourcePreview
              files={files}
              activeItem={highlightItem}
              sx={{ maxHeight: { xs: 480, lg: 'calc(100vh - 240px)' } }}
            />
          </Suspense>
        </Paper>
        </Box>
      )}
    </Box>
  );
};

export default BloodTestAnalysis;
